<template>
  <div class="sales-overview">
    <!-- ===== LOADER ===== -->
    <div v-if="isLoading" class="global-loader">
      <div class="loader-3d">
        <div class="cube">
          <div class="face front"></div>
          <div class="face back"></div>
          <div class="face right"></div>
          <div class="face left"></div>
          <div class="face top"></div>
          <div class="face bottom"></div>
        </div>
      </div>
      <p class="loader-text">Loading...</p>
    </div>

    <template v-else>
      <!-- ===== PAGE HEADER ===== -->
      <div class="page-header">
        <div>
          <h1 class="page-title">Sales Overview</h1>
          <p class="page-subtitle">
            Real-time insights for Furniture Mecca • Updated today, {{ currentDate }}
          </p>
        </div>
        <button class="btn-export">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          Export Report
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
      </div>

      <!-- ===== FILTER BAR ===== -->
      <div class="filter-bar-compact">
        <div class="filter-left">
          <select v-model="selectedDimension" class="filter-select-sm" @change="onDimensionChange">
            <option v-for="dim in dimensionOptions" :key="dim.value" :value="dim.value">{{ dim.text }}</option>
          </select>
          <date-picker
            v-model="dateRange"
            type="date"
            range
            placeholder="Select date range"
            value-type="YYYY-MM-DD"
            format="MMM DD, YYYY"
            class="date-picker-sm"
            @input="fetchData"
          />
          <select v-model="selectedState" class="filter-select-sm" @change="fetchData">
            <option v-for="state in stateOptions" :key="state.value" :value="state.value">{{ state.text }}</option>
          </select>
          <select v-model="pickedLocation" class="filter-select-sm" @change="fetchData">
            <option :value="null">All Locations</option>
            <option v-for="loc in locationOptions" :key="loc.value" :value="loc.value">{{ loc.text }}</option>
          </select>
        </div>
        <div class="filter-right">
          <button class="btn-outline-sm" @click="clearFilters">Clear</button>
          <button class="btn-primary-sm" @click="fetchData" :disabled="isLoading">Refresh</button>
        </div>
      </div>

      <!-- ===== SUMMARY STATS ===== -->
      <div class="summary-stats-new" v-if="summaryData">
        <div class="stat-card-new dark accent-green">
          <div class="stat-icon-box icon-green">
            <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
              <polyline points="17 6 23 6 23 12"/>
            </svg>
          </div>
          <span class="stat-label-new">Total Revenue</span>
          <span class="stat-value-new">{{ formatCurrency(summaryData.total_revenue || 0) }}</span>
          <div class="stat-trend-new">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
              <polyline points="17 6 23 6 23 12"/>
            </svg>
            12.5% <span class="trend-label">vs last month</span>
          </div>
        </div>

        <div class="stat-card-new accent-blue">
          <div class="stat-icon-box icon-blue">
            <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
            </svg>
          </div>
          <span class="stat-label-new">Units Sold</span>
          <span class="stat-value-new">{{ formatNumber(summaryData.total_units_sold || 0) }}</span>
          <div class="stat-trend-new">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
              <polyline points="17 6 23 6 23 12"/>
            </svg>
            8.2% <span class="trend-label">vs last month</span>
          </div>
        </div>

        <div class="stat-card-new accent-purple">
          <div class="stat-icon-box icon-purple">
            <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
              <circle cx="9" cy="21" r="1"/>
              <circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
          </div>
          <span class="stat-label-new">Orders</span>
          <span class="stat-value-new">{{ formatNumber(summaryData.total_orders || 0) }}</span>
          <div class="stat-trend-new">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
              <polyline points="17 6 23 6 23 12"/>
            </svg>
            5.4% <span class="trend-label">vs last month</span>
          </div>
        </div>

        <div class="stat-card-new accent-amber">
          <div class="stat-icon-box icon-green-solid">
            <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
              <polyline points="17 6 23 6 23 12"/>
            </svg>
          </div>
          <span class="stat-label-new">Profit Margin</span>
          <span class="stat-value-new">{{ formatNumber(summaryData.overall_profit_margin || 0) }}%</span>
          <div class="stat-trend-new">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
              <polyline points="17 6 23 6 23 12"/>
            </svg>
            2.1% <span class="trend-label">vs last month</span>
          </div>
        </div>
      </div>

      <!-- ===== CATEGORY PERFORMANCE ===== -->
      <div
        class="category-performance-section"
        v-if="selectedDimension === 'category' && topCategoriesWithProducts.length"
      >
        <div class="section-header-new">
          <h2 class="section-title-new">Category Performance</h2>
          <div class="section-header-right">
            <span class="section-subtitle">Compare by category • This Year 2024</span>
            <select class="filter-select-xs">
              <option>This Year</option>
            </select>
          </div>
        </div>

        <div class="category-cards-grid">
          <div
            v-for="(category, catIdx) in topCategoriesWithProducts"
            :key="catIdx"
            class="category-card-new"
            :class="'accent-' + (catIdx + 1)"
          >
            <div class="category-rank-pill" :class="'pill-' + (catIdx + 1)">
              #{{ catIdx + 1 }} {{ (category.category_name || 'UNCATEGORIZED').toUpperCase() }}
            </div>

            <div class="category-revenue-new">{{ formatCurrency(category.total_revenue) }}</div>
            <div class="category-units-new">
              Revenue • {{ formatNumber(category.total_units) }} units
            </div>

            <div class="category-products-list">
              <div
                v-for="(product, prodIdx) in (category.products || []).slice(0, 2)"
                :key="prodIdx"
                class="product-row-new"
              >
                <!-- ✅ PROXIED IMAGE with direct fallback -->
                <div class="product-thumb-new" v-if="getImageUrl(product.image_url)">
                  <img
                    :src="getImageUrl(product.image_url)"
                    :data-original-url="product.image_url"
                    :alt="product.product_name"
                    @error="handleImageError"
                  >
                </div>
                <div class="product-thumb-new placeholder" v-else>
                  {{ getInitials(product.product_name) }}
                </div>
                <div class="product-text-new">
                  <!-- ✅ FULL NAME, no truncation -->
                  <div class="product-name-new" :title="product.product_name">
                    {{ product.product_name || 'Unknown' }}
                  </div>
                  <div class="product-price-new">{{ formatCurrency(product.revenue || 0) }}</div>
                  <div class="product-meta-new">
                    {{ formatNumber(product.units_sold || 0) }} sold • ★ 4.7
                  </div>
                </div>
              </div>
              <div v-if="!category.products.length" class="empty-products">No products</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== TABLE ===== -->
      <div class="section-container" style="margin-top: 32px;">
        <div class="section-title">
          <h2>{{ selectedDimensionLabel }} Performance</h2>
          <div class="table-controls">
            <select v-model="itemsPerPage" class="filter-select-sm" @change="onPerPageChange">
              <option v-for="opt in [10, 25, 50, 100]" :key="opt" :value="opt">{{ opt }}</option>
            </select>
          </div>
        </div>

        <div class="table-responsive-wrapper accent-indigo">
          <table class="modal-table clickable-rows">
            <thead>
              <tr>
                <th style="width: 50px;">#</th>
                <th  style="width: 150px; font-size: 20px;">{{ selectedDimensionLabel }}</th>
                <!-- <th  v-if="selectedDimension !== 'product'" style="width: 150px;">Category</th> -->
                <th class="text-right" style="width: 100px;">Units</th>
                <th class="text-right" style="width: 120px;">Revenue</th>
                <th class="text-right" style="width: 100px;">Avg Price</th>
                <th class="text-right" style="width: 90px;">Profit %</th>
                <th class="text-right" style="width: 80px;">Orders</th>
                <th class="text-right" style="width: 80px;">Products</th>
                <th class="text-center" style="width: 60px;">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in tableData"
                :key="item.dimension_id || index"
                @click="openDetailModal(item)"
              >
                <td>#{{ ((pagination.offset || 0) + index + 1) }}</td>
                <td>
                  <div class="dimension-cell">
                    <!-- ✅ PROXIED IMAGE with direct fallback -->
                    <span v-if="getImageUrl(item.image_url)" class="dimension-thumb">
                      <img
                        :src="getImageUrl(item.image_url)"
                        :data-original-url="item.image_url"
                        :alt="item.dimension_name"
                        @error="handleImageError"
                      >
                    </span>
                    <!-- ✅ FULL NAME, no truncation -->
                    <span class="dimension-name" :title="item.dimension_name">
                      {{ item.dimension_name || 'Unknown' }}
                    </span>
                  </div>
                </td>
                <!-- <td v-if="selectedDimension !== 'product'">
                  <span class="badge-info">{{ item.category_name || 'Uncategorized' }}</span>
                </td> -->
                <td class="text-right">{{ formatNumber(item.sold_in_range || item.total_units_sold || 0) }}</td>
                <td class="text-right font-weight-bold text-primary">
                  {{ formatCurrency(item.revenue_in_range || item.total_revenue || 0) }}
                </td>
                <td class="text-right">{{ formatCurrency(item.avg_selling_price || 0) }}</td>
                <td class="text-right">
                  <span :class="getProfitClass(item.avg_profit_margin || 0)">
                    {{ formatNumber(item.avg_profit_margin || 0) }}%
                  </span>
                </td>
                <td class="text-right">{{ formatNumber(item.order_count || 0) }}</td>
                <td class="text-right">{{ formatNumber(item.product_count || 0) }}</td>
                <td class="text-center">
                  <button class="btn-icon" @click.stop="openDetailModal(item)">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  </button>
                </td>
              </tr>
              <tr v-if="!tableData || !tableData.length">
                <td :colspan="selectedDimension !== 'product' ? 10 : 9" class="text-center text-muted py-4">
                  No data available for the selected filters
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pagination-wrapper" v-if="totalItems > 0">
          <div class="pagination">
            <button class="page-btn" :disabled="currentPage === 1" @click="onPageChange(currentPage - 1)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              class="page-btn"
              :class="{ active: page === currentPage }"
              @click="onPageChange(page)"
            >
              {{ page }}
            </button>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="onPageChange(currentPage + 1)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- ===== CHARTS ===== -->
      <div class="section-grid-2" style="margin-top: 32px;">
        <div class="chart-card accent-indigo">
          <div class="chart-header"><h6>Revenue by {{ selectedDimensionLabel }}</h6></div>
          <div class="chart-container" style="height: 280px;">
            <canvas ref="revenueBarChart"></canvas>
          </div>
        </div>
        <div class="chart-card accent-emerald">
          <div class="chart-header"><h6>Revenue Distribution</h6></div>
          <div class="chart-container" style="height: 280px;">
            <canvas ref="revenuePieChart"></canvas>
          </div>
        </div>
      </div>

      <div class="section-grid-2">
        <div class="chart-card accent-emerald">
          <div class="chart-header"><h6>Units Sold by {{ selectedDimensionLabel }}</h6></div>
          <div class="chart-container" style="height: 280px;">
            <canvas ref="unitsBarChart"></canvas>
          </div>
        </div>
        <div class="chart-card accent-amber">
          <div class="chart-header"><h6>Profit Margin by {{ selectedDimensionLabel }}</h6></div>
          <div class="chart-container" style="height: 280px;">
            <canvas ref="profitMarginChart"></canvas>
          </div>
        </div>
      </div>
    </template>

    <!-- ===== DETAIL MODAL ===== -->
    <div v-if="showDetailModal" class="modal-overlay" @click.self="showDetailModal = false">
      <div class="modal-container">
        <div class="modal-header">
          <h3>{{ detailModalTitle }}</h3>
          <button class="modal-close" @click="showDetailModal = false">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="isDetailLoading" class="modal-loader">
            <div class="loader-3d small">
              <div class="cube">
                <div class="face front"></div>
                <div class="face back"></div>
                <div class="face right"></div>
                <div class="face left"></div>
                <div class="face top"></div>
                <div class="face bottom"></div>
              </div>
            </div>
            <p class="loader-text">Loading details...</p>
          </div>
          <div v-else-if="detailData">
            <div class="modal-summary-grid">
              <div class="modal-summary-item accent-indigo">
                <div class="summary-icon total">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20 7h-4a2 2 0 0 1-2-2V1"/>
                    <path d="M4 7V4a2 2 0 0 1 2-2h7l7 7v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7z"/>
                  </svg>
                </div>
                <div>
                  <span class="label">Products</span>
                  <span class="value">{{ formatNumber(detailData.summary.total_products || 0) }}</span>
                </div>
              </div>
              <div class="modal-summary-item accent-emerald">
                <div class="summary-icon revenue">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="12" y1="1" x2="12" y2="23"/>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                </div>
                <div>
                  <span class="label">Revenue</span>
                  <span class="value">{{ formatCurrency(detailData.summary.total_revenue || 0) }}</span>
                </div>
              </div>
              <div class="modal-summary-item accent-amber">
                <div class="summary-icon units">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                  </svg>
                </div>
                <div>
                  <span class="label">Units Sold</span>
                  <span class="value">{{ formatNumber(detailData.summary.total_units_sold || 0) }}</span>
                </div>
              </div>
              <div class="modal-summary-item accent-purple">
                <div class="summary-icon avg-price">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 6v12"/>
                    <path d="M16 8a4 4 0 0 0-8 0 4 4 0 0 0 8 0z"/>
                  </svg>
                </div>
                <div>
                  <span class="label">Avg Price</span>
                  <span class="value">{{ formatCurrency(detailData.summary.avg_price || 0) }}</span>
                </div>
              </div>
            </div>

            <div class="modal-charts-row">
              <div class="chart-card accent-purple">
                <div class="chart-header"><h6>Product Revenue Distribution</h6></div>
                <div class="chart-container" style="height: 200px;">
                  <canvas ref="modalPieChart"></canvas>
                </div>
              </div>
              <div class="chart-card accent-indigo">
                <div class="chart-header"><h6>Revenue by Location</h6></div>
                <div class="chart-container" style="height: 200px;">
                  <canvas ref="modalLocationChart"></canvas>
                </div>
              </div>
            </div>

            <div class="modal-tabs accent-indigo">
              <div class="modal-tab-nav">
                <button class="modal-tab-btn" :class="{ active: modalActiveTab === 'products' }" @click="modalActiveTab = 'products'">📦 Products</button>
                <button class="modal-tab-btn" :class="{ active: modalActiveTab === 'locations' }" @click="modalActiveTab = 'locations'">📍 Locations</button>
                <button class="modal-tab-btn" :class="{ active: modalActiveTab === 'salespeople' }" @click="modalActiveTab = 'salespeople'">👤 Salespeople</button>
              </div>
              <div class="modal-tab-content">
                <div v-show="modalActiveTab === 'products'" class="table-responsive-wrapper">
                  <table class="modal-table">
                    <thead>
                      <tr>
                        <th>#</th><th>Product</th><th>SKU</th>
                        <th class="text-right">Units</th><th class="text-right">Revenue</th>
                        <th class="text-right">Avg Price</th><th class="text-right">Profit %</th>
                       
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(p, idx) in (detailData.products || [])" :key="p.sku || idx">
                        <td>#{{ idx + 1 }}</td>
                        <td>
                          <div class="product-cell">
                            <!-- ✅ PROXIED IMAGE with direct fallback -->
                            <div class="product-thumb">
                              <img
                                :src="getImageUrl(p.image_url)"
                                :data-original-url="p.image_url"
                                :alt="p.product_name"
                                @error="handleImageError"
                              >
                            </div>
                            <!-- ✅ FULL NAME, no truncation -->
                            <span class="product-name" :title="p.product_name">
                              {{ p.product_name || 'Unknown' }}
                            </span>
                          </div>
                        </td>
                        <td><code>{{ p.sku || 'N/A' }}</code></td>
                        <td class="text-right">{{ formatNumber(p.units_sold || 0) }}</td>
                        <td class="text-right font-weight-bold text-primary">{{ formatCurrency(p.revenue || 0) }}</td>
                        <td class="text-right">{{ formatCurrency(p.avg_price || 0) }}</td>
                        <td class="text-right">
                          <span :class="getProfitClass(p.profit_margin || 0)">
                            {{ formatNumber(p.profit_margin || 0) }}%
                          </span>
                        </td>
                      </tr>
                      <tr v-if="!detailData.products || !detailData.products.length">
                        <td colspan="8" class="text-center text-muted">No products found</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-show="modalActiveTab === 'locations'" class="table-responsive-wrapper">
                  <table class="modal-table">
                    <thead>
                      <tr>
                        <th>Location</th><th class="text-right">Units Sold</th>
                        <th class="text-right">Revenue</th><th class="text-right">Orders</th>
                        <th class="text-right">% of Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="loc in (detailData.locations || [])" :key="loc.location_id">
                        <td><strong>{{ loc.location_name || 'Unknown' }}</strong></td>
                        <td class="text-right">{{ formatNumber(loc.units_sold || 0) }}</td>
                        <td class="text-right font-weight-bold text-primary">{{ formatCurrency(loc.revenue || 0) }}</td>
                        <td class="text-right">{{ formatNumber(loc.orders || 0) }}</td>
                        <td class="text-right">{{ calculatePercentage(loc.revenue, detailData.summary.total_revenue) }}%</td>
                      </tr>
                      <tr v-if="!detailData.locations || !detailData.locations.length">
                        <td colspan="5" class="text-center text-muted">No location data available</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-show="modalActiveTab === 'salespeople'" class="table-responsive-wrapper">
                  <table class="modal-table">
                    <thead>
                      <tr>
                        <th>Salesperson</th><th class="text-right">Units Sold</th>
                        <th class="text-right">Revenue</th><th class="text-right">Orders</th>
                        <th class="text-right">Commission</th><th class="text-right">Avg Order Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="sp in (detailData.salespeople || [])" :key="sp.salesperson_name">
                        <td><strong>{{ sp.salesperson_name || 'Unknown' }}</strong></td>
                        <td class="text-right">{{ formatNumber(sp.units_sold || 0) }}</td>
                        <td class="text-right font-weight-bold text-primary">{{ formatCurrency(sp.revenue || 0) }}</td>
                        <td class="text-right">{{ formatNumber(sp.orders || 0) }}</td>
                        <td class="text-right">{{ formatCurrency(sp.commission || 0) }}</td>
                        <td class="text-right">{{ formatCurrency((sp.revenue || 0) / (sp.orders || 1)) }}</td>
                      </tr>
                      <tr v-if="!detailData.salespeople || !detailData.salespeople.length">
                        <td colspan="6" class="text-center text-muted">No salesperson data available</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="showDetailModal = false">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';
import Chart from 'chart.js';
import Requestor from '@/utilities/Requestor';

export default {
  name: 'ProductStatsPage',
  components: { DatePicker },
  props: {
    userDetails: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      isLoading: true,
      modalActiveTab: 'products',
      selectedDimension: 'category',
      dimensionOptions: [
        { value: 'category', text: 'Category' },
        { value: 'subcategory', text: 'Subcategory' },
        { value: 'collection', text: 'Collection' },
        { value: 'vendor', text: 'Vendor' },
        { value: 'type', text: 'Type' },
        { value: 'product', text: 'Product' }
      ],
      dateRange: [
        moment().subtract(30, 'days').format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD')
      ],
      selectedState: null,
      pickedLocation: null,
      summaryData: null,
      tableData: [],
      vendorData: [],
      totalItems: 0,
      currentPage: 1,
      itemsPerPage: 25,
      pagination: { limit: 25, offset: 0 },
      showDetailModal: false,
      isDetailLoading: false,
      detailModalTitle: '',
      detailData: null,
      charts: {
        revenueBar: null,
        revenuePie: null,
        unitsBar: null,
        profitMargin: null,
        modalPie: null,
        modalLocation: null
      },
      stateOptions: [
        { value: null, text: 'All States' },
        { value: 1, text: 'DELIVERED' },
        { value: 0, text: 'ORDERED' },
        { value: 2, text: 'PULLED' },
        { value: '-33', text: 'RETURNED' },
        { value: 32, text: 'CANCELLED' },
        { value: 15, text: 'PENDING' }
      ],
      locationMap: {},
      topCategoriesWithProducts: [],
      topSubcategoriesWithProducts: [],
      topCollectionsWithProducts: [],
      topVendorsWithProducts: [],
      topTypesWithProducts: [],
      isLoadingTopItems: false
    };
  },
  computed: {
    selectedDimensionLabel() {
      const opt = this.dimensionOptions.find(d => d.value === this.selectedDimension);
      return opt ? opt.text : 'Items';
    },
    locationOptions() {
      return Object.entries(this.locationMap).map(([id, name]) => ({
        value: parseInt(id),
        text: name
      }));
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i);
      } else {
        pages.push(1);
        if (current > 3) pages.push('...');
        for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
          pages.push(i);
        }
        if (current < total - 2) pages.push('...');
        pages.push(total);
      }
      return pages;
    },
    currentDate() {
      return moment().format('MMM D, YYYY • h:mm A');
    }
  },
  created() {
    if (!this.userDetails || !this.userDetails.username) {
      try {
        const storedUser = JSON.parse(localStorage.userDetails || "{}");
        if (storedUser && storedUser.username) {
          this.$set(this, 'userDetails', storedUser);
        }
      } catch (err) {
        console.error('Failed to load userDetails:', err);
      }
    }
  },
  mounted() {
    this.loadLocationMap();
    this.fetchData();
  },
  beforeDestroy() {
    Object.keys(this.charts).forEach(key => {
      if (this.charts[key]) {
        this.charts[key].destroy();
        this.charts[key] = null;
      }
    });
  },
  methods: {
    /* ============================================================
     * IMAGE HELPERS
     * ============================================================ */

    getImageUrl(imageUrl) {
      if (!imageUrl) return null;
      const url = String(imageUrl).trim();
      if (!url || url === 'null' || url === 'undefined') return null;
      return `/api/image/image-proxy/?url=${encodeURIComponent(url)}`;
    },

    handleImageError(event) {
      const img = event.target;
      const originalUrl = img.getAttribute('data-original-url');

      if (img.dataset.fallbackTried === '1') {
        img.style.display = 'none';
        return;
      }

      if (originalUrl) {
        img.dataset.fallbackTried = '1';
        img.src = originalUrl;
      } else {
        img.style.display = 'none';
      }
    },

    /* ============================================================
     * FORMATTERS / UTILS
     * ============================================================ */

    truncateText(text, maxLength) {
      if (!text) return 'N/A';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },
    getInitials(name) {
      if (!name) return '?';
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    formatNumber(num) {
      if (num === undefined || num === null) return '0';
      return Number(num).toLocaleString('en-US', {
        minimumFractionDigits: num % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      });
    },
    formatCurrency(value) {
      if (value === undefined || value === null) return '$0.00';
      return `$${parseFloat(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    },
    calculatePercentage(value, total) {
      if (!total || total === 0) return 0;
      return ((value / total) * 100).toFixed(1);
    },
    getProfitClass(margin) {
      const m = parseFloat(margin) || 0;
      if (m > 30) return 'text-success';
      if (m > 15) return 'text-warning';
      return 'text-danger';
    },

    /* ============================================================
     * DATA LOADING
     * ============================================================ */

    loadLocationMap() {
      try {
        const locations = JSON.parse(localStorage.getItem('locations') || '[]');
        locations.forEach(loc => {
          this.locationMap[loc.value] = loc.text;
        });
        if (Object.keys(this.locationMap).length === 0) {
          this.locationMap = {
            16: "HUNTING PARK",
            44: "ONLINE SALES",
            29: "LANCASTER",
            25: "ALLENTOWN",
            27: "READING",
            28: "EWING",
            22: "LANSDOWNE",
            17: "UPPER DARBY",
            46: "OUT OF STOCK",
            40: "9TH ST",
            36: "VENANGO",
            24: "DELMAR",
            47: "MECCA SHIPPING",
            42: "101 WH",
            41: "FOLCROFT"
          };
        }
      } catch (e) {
        console.error('Error loading locations:', e);
      }
    },
    makeRequest(url, data) {
      return new Promise((resolve, reject) => {
        Requestor.postSyncP(url, data, this,
          (response) => {
            if (response && response.data) {
              if (response.data.success === true) {
                resolve(response.data);
              } else if (response.data.success === false) {
                reject(new Error(response.data.error || 'Request failed'));
              } else if (response.data.result !== undefined) {
                resolve(response.data);
              } else {
                resolve(response.data);
              }
            } else if (response && response.result !== undefined) {
              resolve({ result: response.result, success: true });
            } else if (response && response.success === false) {
              reject(new Error(response.error || 'Request failed'));
            } else if (response) {
              resolve({ result: response, success: true });
            } else {
              reject(new Error('No response received'));
            }
          },
          (error) => {
            console.error('Request error:', error);
            reject(error);
          }
        );
      });
    },
    onDimensionChange() {
      this.currentPage = 1;
      this.pagination.offset = 0;
      this.fetchData();
    },
    onPerPageChange() {
      this.currentPage = 1;
      this.pagination.offset = 0;
      this.pagination.limit = this.itemsPerPage;
      this.fetchData();
    },
    onPageChange(page) {
      if (page === '...') return;
      this.currentPage = page;
      this.pagination.offset = (page - 1) * this.itemsPerPage;
      this.fetchData();
    },
    clearFilters() {
      this.dateRange = [
        moment().subtract(30, 'days').format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD')
      ];
      this.selectedState = null;
      this.pickedLocation = null;
      this.currentPage = 1;
      this.pagination.offset = 0;
      this.fetchData();
    },
    async fetchData() {
      this.isLoading = true;
      try {
        const username = this.userDetails.username || this.userDetails.name || '';
        if (!username) {
          console.warn('No username found');
          this.isLoading = false;
          return;
        }
        const params = {
          username: username,
          dimension: this.selectedDimension,
          date_range: this.getDateRangeKey(),
          limit: this.itemsPerPage,
          offset: this.pagination.offset,
          user_id: this.userDetails.id || ''
        };
        if (this.selectedState !== null && this.selectedState !== undefined && this.selectedState !== '') {
          params.state = this.selectedState;
        }
        if (this.pickedLocation) {
          params.location_id = this.pickedLocation;
        }
        const response = await this.makeRequest('/api/product/get_product_stats_by_dimension/', params);
        if (response && response.result) {
          this.summaryData = response.result.summary || null;
          this.tableData = (response.result.data || []).map(item => ({
            ...item,
            display_revenue: item.revenue_in_range || item.total_revenue || 0,
            display_units: item.sold_in_range || item.total_units_sold || 0
          }));
          this.totalItems = response.result.pagination.total || 0;
          this.pagination = response.result.pagination || { limit: this.itemsPerPage, offset: 0 };
          if (this.selectedDimension === 'vendor') {
            this.vendorData = this.tableData;
          } else {
            this.vendorData = this.extractVendorData();
          }
        }
        await this.fetchTopItems();
        this.$nextTick(() => {
          setTimeout(() => {
            this.renderAllCharts();
          }, 300);
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchTopItems() {
      this.isLoadingTopItems = true;
      try {
        const username = this.userDetails.username || this.userDetails.name || '';
        const dimensionMap = {
          'category': { fetch: this.fetchTopCategories },
          'subcategory': { fetch: this.fetchTopSubcategories },
          'collection': { fetch: this.fetchTopCollections },
          'vendor': { fetch: this.fetchTopVendors },
          'type': { fetch: this.fetchTopTypes }
        };
        const config = dimensionMap[this.selectedDimension];
        if (config) {
          await config.fetch.call(this, username);
        } else {
          this.topCategoriesWithProducts = [];
          this.topSubcategoriesWithProducts = [];
          this.topCollectionsWithProducts = [];
          this.topVendorsWithProducts = [];
          this.topTypesWithProducts = [];
        }
      } catch (error) {
        console.error('Error fetching top items:', error);
      } finally {
        this.isLoadingTopItems = false;
      }
    },
    async fetchTopCategories(username) {
      try {
        const params = {
          username: username,
          dimension: 'category',
          date_range: this.getDateRangeKey(),
          limit: 4,
          offset: 0,
          user_id: this.userDetails.id || '',
          sort_by: 'revenue',
          sort_order: 'desc'
        };
        if (this.selectedState !== null && this.selectedState !== undefined && this.selectedState !== '') {
          params.state = this.selectedState;
        }
        if (this.pickedLocation) {
          params.location_id = this.pickedLocation;
        }
        const response = await this.makeRequest('/api/product/get_product_stats_by_dimension/', params);
        if (response.result && response.result.data) {
          const topItems = response.result.data;
          const itemPromises = topItems.map(async (item) => {
            const productParams = {
              username: username,
              dimension: 'product',
              date_range: this.getDateRangeKey(),
              limit: 2,
              offset: 0,
              user_id: this.userDetails.id || '',
              category_id: item.dimension_id,
              sort_by: 'revenue',
              sort_order: 'desc'
            };
            if (this.selectedState !== null && this.selectedState !== undefined && this.selectedState !== '') {
              productParams.state = this.selectedState;
            }
            if (this.pickedLocation) {
              productParams.location_id = this.pickedLocation;
            }
            try {
              const productResponse = await this.makeRequest('/api/product/get_product_stats_by_dimension/', productParams);
              let products = [];
              if (productResponse.result && productResponse.result.data) {
                products = productResponse.result.data.map(p => ({
                  product_name: p.dimension_name || p.product_name || 'Unknown',
                  units_sold: p.sold_in_range || p.total_units_sold || 0,
                  revenue: p.revenue_in_range || p.total_revenue || 0,
                  profit_margin: p.avg_profit_margin || 0,
                  image_url: p.image_url || '',
                  category_name: p.category_name || item.dimension_name,
                  dimension_id: p.dimension_id,
                  sku: p.sku || p.dimension_id
                }));
              }
              return {
                category_name: item.dimension_name || 'Unknown',
                category_id: item.dimension_id,
                total_revenue: item.revenue_in_range || item.total_revenue || 0,
                total_units: item.sold_in_range || item.total_units_sold || 0,
                total_products: item.product_count || 0,
                avg_profit: item.avg_profit_margin || 0,
                products: products
              };
            } catch (err) {
              console.error(`Error fetching products for ${item.dimension_name}:`, err);
              return {
                category_name: item.dimension_name || 'Unknown',
                category_id: item.dimension_id,
                total_revenue: item.revenue_in_range || item.total_revenue || 0,
                total_units: item.sold_in_range || item.total_units_sold || 0,
                total_products: item.product_count || 0,
                avg_profit: item.avg_profit_margin || 0,
                products: []
              };
            }
          });
          this.topCategoriesWithProducts = await Promise.all(itemPromises);
        } else {
          this.topCategoriesWithProducts = [];
        }
      } catch (error) {
        console.error('Error fetching top categories:', error);
        this.topCategoriesWithProducts = [];
      }
    },
    async fetchTopSubcategories(username) {
      try {
        const params = {
          username: username,
          dimension: 'subcategory',
          date_range: this.getDateRangeKey(),
          limit: 4,
          offset: 0,
          user_id: this.userDetails.id || '',
          sort_by: 'revenue',
          sort_order: 'desc'
        };
        if (this.selectedState !== null && this.selectedState !== undefined && this.selectedState !== '') {
          params.state = this.selectedState;
        }
        if (this.pickedLocation) {
          params.location_id = this.pickedLocation;
        }
        const response = await this.makeRequest('/api/product/get_product_stats_by_dimension/', params);
        if (response.result && response.result.data) {
          const topItems = response.result.data;
          const itemPromises = topItems.map(async (item) => {
            const productParams = {
              username: username,
              dimension: 'product',
              date_range: this.getDateRangeKey(),
              limit: 2,
              offset: 0,
              user_id: this.userDetails.id || '',
              subcategory_id: item.dimension_id,
              sort_by: 'revenue',
              sort_order: 'desc'
            };
            if (this.selectedState !== null && this.selectedState !== undefined && this.selectedState !== '') {
              productParams.state = this.selectedState;
            }
            if (this.pickedLocation) {
              productParams.location_id = this.pickedLocation;
            }
            try {
              const productResponse = await this.makeRequest('/api/product/get_product_stats_by_dimension/', productParams);
              let products = [];
              if (productResponse.result && productResponse.result.data) {
                products = productResponse.result.data.map(p => ({
                  product_name: p.dimension_name || p.product_name || 'Unknown',
                  units_sold: p.sold_in_range || p.total_units_sold || 0,
                  revenue: p.revenue_in_range || p.total_revenue || 0,
                  profit_margin: p.avg_profit_margin || 0,
                  image_url: p.image_url || '',
                  category_name: p.category_name || item.category_name,
                  dimension_id: p.dimension_id,
                  sku: p.sku || p.dimension_id
                }));
              }
              return {
                subcategory_name: item.dimension_name || 'Unknown',
                subcategory_id: item.dimension_id,
                total_revenue: item.revenue_in_range || item.total_revenue || 0,
                total_units: item.sold_in_range || item.total_units_sold || 0,
                total_products: item.product_count || 0,
                avg_profit: item.avg_profit_margin || 0,
                products: products
              };
            } catch (err) {
              console.error(`Error fetching products for subcategory ${item.dimension_name}:`, err);
              return {
                subcategory_name: item.dimension_name || 'Unknown',
                subcategory_id: item.dimension_id,
                total_revenue: item.revenue_in_range || item.total_revenue || 0,
                total_units: item.sold_in_range || item.total_units_sold || 0,
                total_products: item.product_count || 0,
                avg_profit: item.avg_profit_margin || 0,
                products: []
              };
            }
          });
          this.topSubcategoriesWithProducts = await Promise.all(itemPromises);
        } else {
          this.topSubcategoriesWithProducts = [];
        }
      } catch (error) {
        console.error('Error fetching top subcategories:', error);
        this.topSubcategoriesWithProducts = [];
      }
    },
    async fetchTopCollections(username) {
      try {
        const params = {
          username: username,
          dimension: 'collection',
          date_range: this.getDateRangeKey(),
          limit: 4,
          offset: 0,
          user_id: this.userDetails.id || '',
          sort_by: 'revenue',
          sort_order: 'desc'
        };
        if (this.selectedState !== null && this.selectedState !== undefined && this.selectedState !== '') {
          params.state = this.selectedState;
        }
        if (this.pickedLocation) {
          params.location_id = this.pickedLocation;
        }
        const response = await this.makeRequest('/api/product/get_product_stats_by_dimension/', params);
        if (response.result && response.result.data) {
          const topItems = response.result.data;
          const itemPromises = topItems.map(async (item) => {
            const productParams = {
              username: username,
              dimension: 'product',
              date_range: this.getDateRangeKey(),
              limit: 2,
              offset: 0,
              user_id: this.userDetails.id || '',
              collection_id: item.dimension_id,
              sort_by: 'revenue',
              sort_order: 'desc'
            };
            if (this.selectedState !== null && this.selectedState !== undefined && this.selectedState !== '') {
              productParams.state = this.selectedState;
            }
            if (this.pickedLocation) {
              productParams.location_id = this.pickedLocation;
            }
            try {
              const productResponse = await this.makeRequest('/api/product/get_product_stats_by_dimension/', productParams);
              let products = [];
              if (productResponse.result && productResponse.result.data) {
                products = productResponse.result.data.map(p => ({
                  product_name: p.dimension_name || p.product_name || 'Unknown',
                  units_sold: p.sold_in_range || p.total_units_sold || 0,
                  revenue: p.revenue_in_range || p.total_revenue || 0,
                  profit_margin: p.avg_profit_margin || 0,
                  image_url: p.image_url || '',
                  category_name: p.category_name || item.category_name,
                  dimension_id: p.dimension_id,
                  sku: p.sku || p.dimension_id
                }));
              }
              return {
                collection_name: item.dimension_name || 'Unknown',
                collection_id: item.dimension_id,
                total_revenue: item.revenue_in_range || item.total_revenue || 0,
                total_units: item.sold_in_range || item.total_units_sold || 0,
                total_products: item.product_count || 0,
                avg_profit: item.avg_profit_margin || 0,
                products: products
              };
            } catch (err) {
              console.error(`Error fetching products for collection ${item.dimension_name}:`, err);
              return {
                collection_name: item.dimension_name || 'Unknown',
                collection_id: item.dimension_id,
                total_revenue: item.revenue_in_range || item.total_revenue || 0,
                total_units: item.sold_in_range || item.total_units_sold || 0,
                total_products: item.product_count || 0,
                avg_profit: item.avg_profit_margin || 0,
                products: []
              };
            }
          });
          this.topCollectionsWithProducts = await Promise.all(itemPromises);
        } else {
          this.topCollectionsWithProducts = [];
        }
      } catch (error) {
        console.error('Error fetching top collections:', error);
        this.topCollectionsWithProducts = [];
      }
    },
    async fetchTopVendors(username) {
      try {
        const params = {
          username: username,
          dimension: 'vendor',
          date_range: this.getDateRangeKey(),
          limit: 4,
          offset: 0,
          user_id: this.userDetails.id || '',
          sort_by: 'revenue',
          sort_order: 'desc'
        };
        if (this.selectedState !== null && this.selectedState !== undefined && this.selectedState !== '') {
          params.state = this.selectedState;
        }
        if (this.pickedLocation) {
          params.location_id = this.pickedLocation;
        }
        const response = await this.makeRequest('/api/product/get_product_stats_by_dimension/', params);
        if (response.result && response.result.data) {
          const topItems = response.result.data;
          const itemPromises = topItems.map(async (item) => {
            const productParams = {
              username: username,
              dimension: 'product',
              date_range: this.getDateRangeKey(),
              limit: 2,
              offset: 0,
              user_id: this.userDetails.id || '',
              vendor_id: item.dimension_id,
              sort_by: 'revenue',
              sort_order: 'desc'
            };
            if (this.selectedState !== null && this.selectedState !== undefined && this.selectedState !== '') {
              productParams.state = this.selectedState;
            }
            if (this.pickedLocation) {
              productParams.location_id = this.pickedLocation;
            }
            try {
              const productResponse = await this.makeRequest('/api/product/get_product_stats_by_dimension/', productParams);
              let products = [];
              if (productResponse.result && productResponse.result.data) {
                products = productResponse.result.data.map(p => ({
                  product_name: p.dimension_name || p.product_name || 'Unknown',
                  units_sold: p.sold_in_range || p.total_units_sold || 0,
                  revenue: p.revenue_in_range || p.total_revenue || 0,
                  profit_margin: p.avg_profit_margin || 0,
                  image_url: p.image_url || '',
                  category_name: p.category_name || item.category_name,
                  dimension_id: p.dimension_id,
                  sku: p.sku || p.dimension_id
                }));
              }
              return {
                vendor_name: item.dimension_name || 'Unknown',
                vendor_id: item.dimension_id,
                total_revenue: item.revenue_in_range || item.total_revenue || 0,
                total_units: item.sold_in_range || item.total_units_sold || 0,
                total_products: item.product_count || 0,
                avg_profit: item.avg_profit_margin || 0,
                products: products
              };
            } catch (err) {
              console.error(`Error fetching products for vendor ${item.dimension_name}:`, err);
              return {
                vendor_name: item.dimension_name || 'Unknown',
                vendor_id: item.dimension_id,
                total_revenue: item.revenue_in_range || item.total_revenue || 0,
                total_units: item.sold_in_range || item.total_units_sold || 0,
                total_products: item.product_count || 0,
                avg_profit: item.avg_profit_margin || 0,
                products: []
              };
            }
          });
          this.topVendorsWithProducts = await Promise.all(itemPromises);
        } else {
          this.topVendorsWithProducts = [];
        }
      } catch (error) {
        console.error('Error fetching top vendors:', error);
        this.topVendorsWithProducts = [];
      }
    },
    async fetchTopTypes(username) {
      try {
        const params = {
          username: username,
          dimension: 'type',
          date_range: this.getDateRangeKey(),
          limit: 4,
          offset: 0,
          user_id: this.userDetails.id || '',
          sort_by: 'revenue',
          sort_order: 'desc'
        };
        if (this.selectedState !== null && this.selectedState !== undefined && this.selectedState !== '') {
          params.state = this.selectedState;
        }
        if (this.pickedLocation) {
          params.location_id = this.pickedLocation;
        }
        const response = await this.makeRequest('/api/product/get_product_stats_by_dimension/', params);
        if (response.result && response.result.data) {
          const topItems = response.result.data;
          const itemPromises = topItems.map(async (item) => {
            const productParams = {
              username: username,
              dimension: 'product',
              date_range: this.getDateRangeKey(),
              limit: 2,
              offset: 0,
              user_id: this.userDetails.id || '',
              type_id: item.dimension_id,
              sort_by: 'revenue',
              sort_order: 'desc'
            };
            if (this.selectedState !== null && this.selectedState !== undefined && this.selectedState !== '') {
              productParams.state = this.selectedState;
            }
            if (this.pickedLocation) {
              productParams.location_id = this.pickedLocation;
            }
            try {
              const productResponse = await this.makeRequest('/api/product/get_product_stats_by_dimension/', productParams);
              let products = [];
              if (productResponse.result && productResponse.result.data) {
                products = productResponse.result.data.map(p => ({
                  product_name: p.dimension_name || p.product_name || 'Unknown',
                  units_sold: p.sold_in_range || p.total_units_sold || 0,
                  revenue: p.revenue_in_range || p.total_revenue || 0,
                  profit_margin: p.avg_profit_margin || 0,
                  image_url: p.image_url || '',
                  category_name: p.category_name || item.category_name,
                  dimension_id: p.dimension_id,
                  sku: p.sku || p.dimension_id
                }));
              }
              return {
                type_name: item.dimension_name || 'Unknown',
                type_id: item.dimension_id,
                total_revenue: item.revenue_in_range || item.total_revenue || 0,
                total_units: item.sold_in_range || item.total_units_sold || 0,
                total_products: item.product_count || 0,
                avg_profit: item.avg_profit_margin || 0,
                products: products
              };
            } catch (err) {
              console.error(`Error fetching products for type ${item.dimension_name}:`, err);
              return {
                type_name: item.dimension_name || 'Unknown',
                type_id: item.dimension_id,
                total_revenue: item.revenue_in_range || item.total_revenue || 0,
                total_units: item.sold_in_range || item.total_units_sold || 0,
                total_products: item.product_count || 0,
                avg_profit: item.avg_profit_margin || 0,
                products: []
              };
            }
          });
          this.topTypesWithProducts = await Promise.all(itemPromises);
        } else {
          this.topTypesWithProducts = [];
        }
      } catch (error) {
        console.error('Error fetching top types:', error);
        this.topTypesWithProducts = [];
      }
    },
    getDateRangeKey() {
      if (!this.dateRange || !this.dateRange[0] || !this.dateRange[1]) {
        return 'all_time';
      }
      const start = moment(this.dateRange[0]);
      const end = moment(this.dateRange[1]);
      const diff = end.diff(start, 'days');
      if (diff <= 7) return '7_days';
      if (diff <= 30) return '30_days';
      if (diff <= 60) return '60_days';
      if (diff <= 90) return '90_days';
      if (diff <= 120) return '120_days';
      return 'all_time';
    },
    extractVendorData() {
      const vendorMap = {};
      (this.tableData || []).forEach(item => {
        const name = item.dimension_name || '';
        if (name && !vendorMap[name]) {
          vendorMap[name] = { vendor: name, revenue: 0, units: 0, orders: 0, products: 0 };
        }
        if (vendorMap[name]) {
          vendorMap[name].revenue += item.total_revenue || 0;
          vendorMap[name].units += item.total_units_sold || 0;
          vendorMap[name].orders += item.order_count || 0;
          vendorMap[name].products += item.product_count || 0;
        }
      });
      return Object.values(vendorMap).sort((a, b) => b.revenue - a.revenue);
    },
    async openDetailModal(item) {
      if (!item) return;
      let dimensionId = item.dimension_id;
      let dimensionName = item.dimension_name || item.product_name || 'Item';
      if (this.selectedDimension === 'product') {
        dimensionId = item.dimension_id || item.sku;
        dimensionName = item.dimension_name || item.product_name || 'Item';
      }
      this.detailModalTitle = `${dimensionName} - ${this.selectedDimensionLabel} Details`;
      this.showDetailModal = true;
      this.isDetailLoading = true;
      this.modalActiveTab = 'products';
      try {
        const username = this.userDetails.username || this.userDetails.name || '';
        const params = {
          username: username,
          dimension: this.selectedDimension,
          dimension_id: dimensionId,
          date_range: this.getDateRangeKey(),
          user_id: this.userDetails.id || ''
        };
        if (this.selectedState !== null && this.selectedState !== undefined && this.selectedState !== '') {
          params.state = this.selectedState;
        }
        if (this.pickedLocation) {
          params.location_id = this.pickedLocation;
        }
        const response = await this.makeRequest('/api/product/get_dimension_detail/', params);
        if (response && response.result) {
          this.detailData = response.result;
          this.$nextTick(() => {
            setTimeout(() => {
              this.renderModalCharts();
            }, 300);
          });
        }
      } catch (error) {
        console.error('Error fetching detail:', error);
      } finally {
        this.isDetailLoading = false;
      }
    },
    viewProduct(product) {
      this.showDetailModal = false;
      this.$emit('view-product', product);
    },

    /* ============================================================
     * CHARTS
     * ============================================================ */

    renderAllCharts() {
      this.renderRevenueBarChart();
      this.renderRevenuePieChart();
      this.renderUnitsBarChart();
      this.renderProfitMarginChart();
    },
    getChartData() {
      const data = (this.tableData || [])
        .sort((a, b) => (b.revenue_in_range || b.total_revenue || 0) - (a.revenue_in_range || a.total_revenue || 0))
        .slice(0, 10);
      return {
        labels: data.map(d => {
          const name = d.dimension_name || 'Unknown';
          return name.length > 15 ? name.substring(0, 15) + '...' : name;
        }),
        revenue: data.map(d => d.revenue_in_range || d.total_revenue || 0),
        units: data.map(d => d.sold_in_range || d.total_units_sold || 0),
        profit: data.map(d => d.avg_profit_margin || 0),
        orders: data.map(d => d.order_count || 0),
        products: data.map(d => d.product_count || 0),
        fullNames: data.map(d => d.dimension_name || 'Unknown'),
        rawData: data
      };
    },
    renderRevenueBarChart() {
      const canvas = this.$refs.revenueBarChart;
      if (!canvas) return;
      if (this.charts.revenueBar) {
        this.charts.revenueBar.destroy();
        this.charts.revenueBar = null;
      }
      const chartData = this.getChartData();
      if (!chartData.labels.length) return;
      try {
        const ctx = canvas.getContext('2d');
        this.charts.revenueBar = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: chartData.labels,
            datasets: [{
              label: 'Revenue ($)',
              data: chartData.revenue,
              backgroundColor: 'rgba(99, 102, 241, 0.8)',
              borderColor: '#6366f1',
              borderWidth: 2,
              borderRadius: 6
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: { callbacks: { label: (ctx) => this.formatCurrency(ctx.parsed.y) } }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: { callback: (value) => this.formatCurrency(value) },
                grid: { color: 'rgba(0, 0, 0, 0.05)' }
              },
              x: { grid: { display: false }, ticks: { maxRotation: 45, minRotation: 30, font: { size: 10 } } }
            }
          }
        });
      } catch (error) {
        console.error('Error rendering revenue bar chart:', error);
      }
    },
    renderRevenuePieChart() {
      const canvas = this.$refs.revenuePieChart;
      if (!canvas) return;
      if (this.charts.revenuePie) {
        this.charts.revenuePie.destroy();
        this.charts.revenuePie = null;
      }
      const chartData = this.getChartData();
      if (!chartData.labels.length) return;
      const colors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#8b5cf6', '#f97316', '#14b8a6', '#f43f5e'];
      try {
        const ctx = canvas.getContext('2d');
        this.charts.revenuePie = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: chartData.labels,
            datasets: [{
              data: chartData.revenue,
              backgroundColor: colors.slice(0, chartData.labels.length).map(c => c + 'CC'),
              borderColor: colors.slice(0, chartData.labels.length),
              borderWidth: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: { padding: 12, usePointStyle: true, pointStyle: 'circle', font: { size: 10 } }
              },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
                    return `${ctx.label}: ${this.formatCurrency(ctx.parsed)} (${percentage}%)`;
                  }
                }
              }
            },
            cutout: '55%'
          }
        });
      } catch (error) {
        console.error('Error rendering revenue pie chart:', error);
      }
    },
    renderUnitsBarChart() {
      const canvas = this.$refs.unitsBarChart;
      if (!canvas) return;
      if (this.charts.unitsBar) {
        this.charts.unitsBar.destroy();
        this.charts.unitsBar = null;
      }
      const chartData = this.getChartData();
      if (!chartData.labels.length) return;
      try {
        const ctx = canvas.getContext('2d');
        this.charts.unitsBar = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: chartData.labels,
            datasets: [{
              label: 'Units Sold',
              data: chartData.units,
              backgroundColor: 'rgba(16, 185, 129, 0.8)',
              borderColor: '#10b981',
              borderWidth: 2,
              borderRadius: 6
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: { callbacks: { label: (ctx) => `${this.formatNumber(ctx.parsed.y)} units` } }
            },
            scales: {
              y: { beginAtZero: true, grid: { color: 'rgba(0, 0, 0, 0.05)' } },
              x: { grid: { display: false }, ticks: { maxRotation: 45, minRotation: 30, font: { size: 10 } } }
            }
          }
        });
      } catch (error) {
        console.error('Error rendering units bar chart:', error);
      }
    },
    renderProfitMarginChart() {
      const canvas = this.$refs.profitMarginChart;
      if (!canvas) return;
      if (this.charts.profitMargin) {
        this.charts.profitMargin.destroy();
        this.charts.profitMargin = null;
      }
      const chartData = this.getChartData();
      if (!chartData.labels.length) return;
      const colors = chartData.profit.map(m => {
        if (m > 30) return 'rgba(16, 185, 129, 0.8)';
        if (m > 15) return 'rgba(245, 158, 11, 0.8)';
        return 'rgba(239, 68, 68, 0.8)';
      });
      try {
        const ctx = canvas.getContext('2d');
        this.charts.profitMargin = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: chartData.labels,
            datasets: [{
              label: 'Profit Margin (%)',
              data: chartData.profit,
              backgroundColor: colors,
              borderColor: colors.map(c => c.replace('0.8', '1')),
              borderWidth: 2,
              borderRadius: 6
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: { callbacks: { label: (ctx) => `${this.formatNumber(ctx.parsed.y)}%` } }
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: { color: 'rgba(0, 0, 0, 0.05)' },
                ticks: { callback: (value) => value + '%' }
              },
              x: { grid: { display: false }, ticks: { maxRotation: 45, minRotation: 30, font: { size: 10 } } }
            }
          }
        });
      } catch (error) {
        console.error('Error rendering profit margin chart:', error);
      }
    },
    renderModalCharts() {
      this.renderModalPieChart();
      this.renderModalLocationChart();
    },
    renderModalPieChart() {
      const canvas = this.$refs.modalPieChart;
      if (!canvas || !this.detailData || !this.detailData.products) return;
      if (this.charts.modalPie) {
        this.charts.modalPie.destroy();
        this.charts.modalPie = null;
      }
      const products = (this.detailData.products || [])
        .sort((a, b) => (b.revenue || 0) - (a.revenue || 0))
        .slice(0, 8);
      if (!products.length) return;
      const colors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#8b5cf6', '#f97316'];
      try {
        const ctx = canvas.getContext('2d');
        this.charts.modalPie = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: products.map(p => p.product_name || 'Unknown'),
            datasets: [{
              data: products.map(p => p.revenue || 0),
              backgroundColor: colors.slice(0, products.length).map(c => c + 'CC'),
              borderColor: colors.slice(0, products.length),
              borderWidth: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'bottom',
                labels: { padding: 8, font: { size: 9 }, usePointStyle: true, pointStyle: 'circle' }
              },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                    const percentage = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
                    return `${ctx.label}: ${this.formatCurrency(ctx.parsed)} (${percentage}%)`;
                  }
                }
              }
            },
            cutout: '60%'
          }
        });
      } catch (error) {
        console.error('Error rendering modal pie chart:', error);
      }
    },
    renderModalLocationChart() {
      const canvas = this.$refs.modalLocationChart;
      if (!canvas || !this.detailData || !this.detailData.locations) return;
      if (this.charts.modalLocation) {
        this.charts.modalLocation.destroy();
        this.charts.modalLocation = null;
      }
      const locations = (this.detailData.locations || [])
        .sort((a, b) => (b.revenue || 0) - (a.revenue || 0))
        .slice(0, 8);
      if (!locations.length) return;
      const colors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#8b5cf6', '#f97316'];
      try {
        const ctx = canvas.getContext('2d');
        this.charts.modalLocation = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: locations.map(l => {
              const name = l.location_name || 'Unknown';
              return name.length > 10 ? name.substring(0, 10) + '...' : name;
            }),
            datasets: [{
              label: 'Revenue',
              data: locations.map(l => l.revenue || 0),
              backgroundColor: colors.slice(0, locations.length).map(c => c + 'CC'),
              borderColor: colors.slice(0, locations.length),
              borderWidth: 2,
              borderRadius: 4
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: { callbacks: { label: (ctx) => this.formatCurrency(ctx.parsed.y) } }
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: { color: 'rgba(0, 0, 0, 0.05)' },
                ticks: { callback: (value) => this.formatCurrency(value) }
              },
              x: { grid: { display: false }, ticks: { font: { size: 9 } } }
            }
          }
        });
      } catch (error) {
        console.error('Error rendering modal location chart:', error);
      }
    }
  }
};
</script>

<style scoped>
/* ===== RESET ===== */
.sales-overview {
  padding: 28px 32px;
  background: #e8effb;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* ===== LOADER ===== */
.global-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  background: #f5f7fa;
}
.loader-3d { width: 70px; height: 70px; perspective: 600px; }
.loader-3d.small { width: 40px; height: 40px; }
.cube {
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  animation: rotateCube 2s infinite ease-in-out;
}
@keyframes rotateCube {
  0% { transform: rotateX(-30deg) rotateY(0deg); }
  50% { transform: rotateX(-30deg) rotateY(180deg); }
  100% { transform: rotateX(-30deg) rotateY(360deg); }
}
.cube .face {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 2px solid rgba(99, 102, 241, 0.3);
  border-radius: 6px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.8), rgba(139, 92, 246, 0.8));
}
.cube .face.front { transform: translateZ(calc(100% / 2)); }
.cube .face.back { transform: rotateY(180deg) translateZ(calc(100% / 2)); }
.cube .face.right { transform: rotateY(90deg) translateZ(calc(100% / 2)); }
.cube .face.left { transform: rotateY(-90deg) translateZ(calc(100% / 2)); }
.cube .face.top { transform: rotateX(90deg) translateZ(calc(100% / 2)); }
.cube .face.bottom { transform: rotateX(-90deg) translateZ(calc(100% / 2)); }
.loader-text {
  margin-top: 20px;
  color: #64748b;
  font-size: 14px;
  animation: pulseText 1.5s ease-in-out infinite;
}
@keyframes pulseText {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

/* ===== PAGE HEADER ===== */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 22px;
}
.page-title {
  font-size: 45px;
  font-weight: 700;
  color: #0b1220;
  margin: 0 0 6px 0;
  letter-spacing: -0.6px;
}
.page-subtitle {
  font-size: 20px;
  color: #050505;
  margin: 0;
}
.btn-export {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: #0b1220;
  color: #fff;
  border: none;
  border-radius: 9px;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  box-shadow: 0 4px 12px rgba(11, 18, 32, 0.4);
}
.btn-export:hover { background: #1e293b; }

/* ===== FILTER BAR ===== */
.filter-bar-compact {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 22px;
  border: 1px solid #e8edf3;
  flex-wrap: wrap;
  gap: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  position: relative;
  overflow: hidden;
}
.filter-bar-compact::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899);
}
.filter-left { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.filter-right { display: flex; align-items: center; gap: 8px; }
.filter-select-sm,
.filter-select-xs {
  padding: 7px 11px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: #fff;
  font-size: 12.5px;
  color: #1e293b;
  outline: none;
  cursor: pointer;
}
.filter-select-sm:hover { border-color: #94a3b8; }
.date-picker-sm {
  background: #fff;
  border-radius: 7px;
  border: 1px solid #e2e8f0;
  font-size: 12.5px;
}
.btn-outline-sm {
  padding: 7px 13px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: #fff;
  color: #64748b;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
}
.btn-outline-sm:hover { background: #f8fafc; }
.btn-primary-sm {
  padding: 7px 15px;
  border: none;
  border-radius: 7px;
  background: #0b1220;
  color: #fff;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
}
.btn-primary-sm:hover { background: #1e293b; }
.btn-primary-sm:disabled { opacity: 0.6; cursor: not-allowed; }

/* ===== ACCENT STRIPE UTILITY ===== */
/* The highlight line on the top of a card is called an "accent stripe" or "top accent border" */
.accent-green::before { background: linear-gradient(90deg, #10b981, #34d399) !important; }
.accent-blue::before { background: linear-gradient(90deg, #3b82f6, #60a5fa) !important; }
.accent-purple::before { background: linear-gradient(90deg, #8b5cf6, #a78bfa) !important; }
.accent-amber::before { background: linear-gradient(90deg, #f59e0b, #fbbf24) !important; }
.accent-emerald::before { background: linear-gradient(90deg, #10b981, #34d399) !important; }
.accent-indigo::before { background: linear-gradient(90deg, #6366f1, #818cf8) !important; }
.accent-1::before { background: linear-gradient(90deg, #6366f1, #818cf8) !important; }
.accent-2::before { background: linear-gradient(90deg, #f59e0b, #fbbf24) !important; }
.accent-3::before { background: linear-gradient(90deg, #8b5cf6, #a78bfa) !important; }
.accent-4::before { background: linear-gradient(90deg, #10b981, #34d399) !important; }

/* ===== SUMMARY STATS ===== */
.summary-stats-new {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 34px;
}
.stat-card-new {
  background: #fff;
  border-radius: 14px;
  padding: 22px 22px 20px;
  border: 1px solid #e8edf3;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  position: relative;
  overflow: hidden;
}
.stat-card-new::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
}
.stat-card-new:hover {
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.4);
  transform: translateY(-1px);
}
.stat-card-new.dark {
  background: #0b1220;
  border-color: #0b1220;
  box-shadow: 0 4px 16px rgba(11, 18, 32, 0.4);
}
.stat-card-new.dark::before {
  background: linear-gradient(90deg, #10b981, #34d399);
}
.stat-icon-box {
  width: 60px;
  height: 60px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  margin-bottom: 18px;
}
.stat-icon-box.icon-green { background: #10b981; }
.stat-icon-box.icon-blue { background: #3b82f6; }
.stat-icon-box.icon-purple { background: #8b5cf6; }
.stat-icon-box.icon-green-solid { background: #10b981; }
.stat-label-new {
  font-size: 20px;
  color: #64748b;
  font-weight: 500;
  margin-bottom: 6px;
}
.stat-card-new.dark .stat-label-new { color: rgba(255, 255, 255, 0.6); }
.stat-value-new {
  font-size: 60px;
  font-weight: 700;
  color: #0b1220;
  line-height: 1.15;
  margin-bottom: 14px;
  letter-spacing: -0.5px;
}
.stat-card-new.dark .stat-value-new { color: #fff; }
.stat-trend-new {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 22px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  color: #10b981;
  background: #d1fae5;
}
.stat-card-new.dark .stat-trend-new {
  color: #34d399;
  background: rgba(16, 185, 129, 0.18);
}
.trend-label {
  font-weight: 400;
  opacity: 0.75;
  margin-left: 2px;
}

/* ===== CATEGORY PERFORMANCE ===== */
.category-performance-section { margin-bottom: 32px; }
.section-header-new {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
  flex-wrap: wrap;
  gap: 12px;
}
.section-title-new {
  font-size: 38px;
  font-weight: 700;
  color: #0b1220;
  margin: 0;
  letter-spacing: -0.4px;
}
.section-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}
.section-subtitle { font-size: 13px; color: #64748b; }

.category-cards-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}
.category-card-new {
  background: #fff;
  border-radius: 14px;
  padding: 18px 18px 14px;
  border: 1px solid #e8edf3;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  position: relative;
  overflow: hidden;
}
.category-card-new::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
}
.category-card-new:hover {
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.4);
  transform: translateY(-1px);
}

.category-rank-pill {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.4px;
  margin-bottom: 14px;
  white-space: nowrap;
}
.pill-1 { background: #e0e7ff; color: #3730a3; }
.pill-2 { background: #fef3c7; color: #92400e; }
.pill-3 { background: #ede9fe; color: #5b21b6; }
.pill-4 { background: #d1fae5; color: #065f46; }

.category-revenue-new {
  font-size: 42px;
  font-weight: 700;
  color: #0b1220;
  line-height: 1.15;
  letter-spacing: -0.5px;
  margin-bottom: 4px;
}
.category-units-new {
  font-size: 18px;
  color: #64748b;
  margin-bottom: 16px;
}

.category-products-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.product-row-new {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.product-thumb-new {
  width: 84px;
  height: 66px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f1f5f9;
  border: 1px solid #e8edf3;
}
.product-thumb-new img { width: 100%; height: 100%; object-fit: cover; }
.product-thumb-new.placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #8b5cf6;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  border-color: #8b5cf6;
}
.product-text-new { flex: 1; min-width: 0; }

.product-name-new {
  font-size: 15px;
  font-weight: 600;
  color: #0b1220;
  line-height: 1.3;
  margin-bottom: 3px;
  word-break: break-word;
  overflow-wrap: break-word;
}
.product-price-new {
  font-size: 15px;
  font-weight: 700;
  color: #0b1220;
  margin-bottom: 2px;
}
.product-meta-new {
  font-size: 13px;
  color: #0f0f0f;
}
.empty-products {
  text-align: center;
  color: #94a3b8;
  font-size: 11px;
  padding: 10px;
}

/* ===== SECTION / TABLE ===== */
.section-container { margin-bottom: 24px; }
.section-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e8edf3;
}
.section-title h2 {
  font-size: 38px;
  font-weight: 600;
  color: #0b1220;
  margin: 0;
}
.table-controls { display: flex; gap: 8px; }

.table-responsive-wrapper {
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
  border-radius: 8px;
  border: 1px solid #e8edf3;
  width: 100%;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  position: relative;
}
.table-responsive-wrapper.accent-indigo::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #6366f1, #818cf8);
  z-index: 2;
}
.modal-table {
  width: 100%;
  font-size: 12px;
  border-collapse: collapse;
}
.modal-table th,
.modal-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap;
  font-size: 20px;
}
.modal-table th {
  background: #f8fafc;
  font-weight: 600;
  color: #475569;
  position: sticky;
  top: 0;
  z-index: 1;
  font-size: 20px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.modal-table tbody tr:hover { background: #f8fafc; }
.clickable-rows tbody tr { cursor: pointer; }
.clickable-rows tbody tr:hover { background: #eef2ff; }

.dimension-cell { display: flex; align-items: center; gap: 8px; }
.dimension-thumb {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f8fafc;
  border: 1px solid #e8edf3;
}
.dimension-thumb img { width: 100%; height: 100%; object-fit: cover; }

.dimension-name {
  font-weight: 500;
  color: #1e293b;
  font-size: 20px;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
}

.badge-info {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  background: #dbeafe;
  color: #1e40af;
  font-size: 10px;
  font-weight: 500;
}
.text-right { text-align: right; }
.text-center { text-align: center; }
.text-muted { color: #94a3b8; }
.text-primary { color: #6366f1; }
.text-success { color: #10b981; }
.text-warning { color: #f59e0b; }
.text-danger { color: #ef4444; }
.font-weight-bold { font-weight: 700; }

.btn-icon {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #6366f1;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.btn-icon:hover { background: rgba(99, 102, 241, 0.1); }

.pagination-wrapper { display: flex; justify-content: center; padding: 12px 0; }
.pagination { display: flex; gap: 4px; align-items: center; }
.page-btn {
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  color: #64748b;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}
.page-btn:hover:not(:disabled) { background: #f1f5f9; border-color: #cbd5e1; }
.page-btn.active { background: #6366f1; color: #fff; border-color: #6366f1; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* ===== CHART CARDS ===== */
.section-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 24px;
}
.chart-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e8edf3;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  position: relative;
  overflow: hidden;
}
.chart-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
}
.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.chart-header h6 {
  margin: 0;
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
}
.chart-container { position: relative; }

/* ===== MODAL ===== */
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.modal-container {
  background: #fff;
  border-radius: 16px;
  max-width: 1100px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: linear-gradient(135deg, #0f172a, #1e293b);
  color: #fff;
  flex-shrink: 0;
}
.modal-header h3 { margin: 0; font-size: 18px; font-weight: 600; }
.modal-close {
  background: none;
  border: none;
  color: rgba(255,255,255,0.6);
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
}
.modal-close:hover { color: #fff; background: rgba(255,255,255,0.1); }
.modal-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
  background: #f8fafc;
}
.modal-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}
.modal-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 16px;
}
.modal-summary-item {
  background: #fff;
  padding: 12px 14px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.4);
  position: relative;
  overflow: hidden;
}
.modal-summary-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
}
.modal-summary-item .summary-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}
.summary-icon.total { background: linear-gradient(135deg, #6366f1, #8b5cf6); }
.summary-icon.revenue { background: linear-gradient(135deg, #10b981, #34d399); }
.summary-icon.units { background: linear-gradient(135deg, #f59e0b, #fbbf24); }
.summary-icon.avg-price { background: linear-gradient(135deg, #8b5cf6, #a78bfa); }
.modal-summary-item .label {
  display: block;
  font-size: 9px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.modal-summary-item .value {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}
.modal-charts-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
}
.modal-tabs {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  position: relative;
}
.modal-tabs.accent-indigo::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #6366f1, #818cf8);
  z-index: 2;
}
.modal-tab-nav {
  display: flex;
  gap: 4px;
  padding: 12px 16px 0;
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
}
.modal-tab-btn {
  padding: 8px 16px;
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  transition: all 0.2s;
  border-radius: 6px 6px 0 0;
}
.modal-tab-btn:hover { color: #1e293b; background: rgba(99, 102, 241, 0.05); }
.modal-tab-btn.active {
  color: #6366f1;
  border-bottom-color: #6366f1;
  background: rgba(99, 102, 241, 0.1);
}
.modal-tab-content { padding: 16px; }
.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 12px 24px;
  border-top: 1px solid #e2e8f0;
  background: #fff;
  flex-shrink: 0;
}
.btn-secondary {
  padding: 8px 20px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
}
.btn-secondary:hover { background: #f1f5f9; }

.product-cell { display: flex; align-items: center; gap: 6px; }
.product-thumb {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}
.product-thumb img { width: 100%; height: 100%; object-fit: cover; }

.product-name {
  font-weight: 500;
  color: #1e293b;
  font-size: 15px;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 1200px) {
  .summary-stats-new { grid-template-columns: repeat(2, 1fr); }
  .category-cards-grid { grid-template-columns: repeat(2, 1fr); }
  .modal-summary-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 992px) {
  .section-grid-2 { grid-template-columns: 1fr; }
  .modal-charts-row { grid-template-columns: 1fr; }
}
@media (max-width: 768px) {
  .sales-overview { padding: 16px; }
  .page-header { flex-direction: column; gap: 12px; }
  .page-title { font-size: 24px; }
  .summary-stats-new { grid-template-columns: 1fr; }
  .filter-bar-compact { flex-direction: column; align-items: stretch; }
  .section-header-new { flex-direction: column; align-items: flex-start; }
  .category-cards-grid { grid-template-columns: 1fr; }
  .modal-summary-grid { grid-template-columns: 1fr; }
  .modal-container { max-width: 100%; margin: 10px; }
}
@media (max-width: 576px) {
  .stat-value-new { font-size: 24px; }
  .category-revenue-new { font-size: 22px; }
}
</style>


// WEBPACK FOOTER //
// src/components/ProductReports/ProductStatsPage.vue